// app/frontend/data/bookingPortalData.tsx
import React from 'react';
import { Anchor, Text, Image } from '@mantine/core';

export interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
}

export interface FAQSection {
  section: string;
  items: FAQItem[];
}

export const bookingPortalData: FAQSection[] = [
  {
    section: 'Why Use A Booking Portal?',
    items: [
      {
        question: 'Why Use A Booking Portal?',
        answer: (
          <ul>
            <li className="mb-3">
              <strong>Centralized Info:</strong> Have all of your most up to date booking information in one place and
              avoid repetitive sending of the same info to multiple organizers.
            </li>
            <li className="mb-3">
              <strong>Decrease Miscommunication:</strong> Ensure more of a peace of mind knowing that the organizers
              have all the info needed to promote you, create flyers, book your travel, and setup your accommodations to
              your needs.
            </li>
            <li className="mb-3">
              <strong>Simplified Process:</strong> Enjoy streamlined booking tools designed for both artists and
              organizers.
            </li>
          </ul>
        ),
      },
    ],
  },
  {
    section: 'Overview: How It Works',
    items: [
      {
        question: 'Overview: How It Works',
        answer: (
          <ul>
            <li className="mb-3">
              Once you have created{' '}
              <Anchor href="https://wherecanwedance.com/register" target="_blank" c="orange" fw={700}>
                a free user account
              </Anchor>{' '}
              and have an{' '}
              <Anchor href="https://wherecanwedance.com/suggest_artist" target="_blank" c="orange" fw={700}>
                artist profile
              </Anchor>{' '}
              you will be able to enable the artist booking portal by editing the artist and creating a portal password.
            </li>
            <li className="mb-3">
              Once the portal is enabled and you've created your password you can update your portal with all of your
              booking information.
            </li>
            <li className="mb-3">
              Once your booking info is ready, you can share your portal link with event organizers providing them with
              the portal password you created, allowing the organizers to have all of your booking info{' '}
              <strong>in one freaking place!</strong>
            </li>
            <li className="mb-3">
              The current artist booking portal areas are short bio, long bio, promo photos, promo videos, traveler
              info, preferred airports, rooming preferences, foods disliked/allergic to, foods loved, artist rates, &
              payment methods!
            </li>
            <li className="mb-3">
              <Image
                src="/assets/portal-artist.png"
                alt="WCWD Artist Menu"
                style={{ maxWidth: '100%', height: 'auto' }}
                radius="md"
              />
            </li>
            <li className="mb-3">
              Through editing your artist profile, you can also change your portal password at any time as needed.
            </li>
          </ul>
        ),
      },
    ],
  },
  {
    section: 'Steps to Set Up Your Booking Portal',
    items: [
      {
        question: 'Steps to Set Up Your Booking Portal',
        answer: (
          <ol>
            <li className="mb-4">
              <h3 className="font-semibold mb-2">Sign Up or Log In</h3>
              <p className="mb-2">
                Create a <strong>FREE</strong>{' '}
                <Anchor href="http://www.wherecanwedance.com/register" c="orange" fw={700}>
                  user account
                </Anchor>{' '}
                on WhereCanWeDance or{' '}
                <Anchor href="http://www.wherecanwedance.com/login" c="orange" fw={700}>
                  log in
                </Anchor>{' '}
                to your existing account to get started.
              </p>
            </li>
            <li className="mb-4">
              <h3 className="font-semibold mb-2">Access the Edit Artist Page</h3>
              <p className="mb-2">
                If you already have an artist profile on WCWD (
                <em>
                  if not you can create a <strong>free</strong> one{' '}
                  <Anchor href="https://wherecanwedance.com/suggest_artist" target="_blank" c="orange" fw={700}>
                    here
                  </Anchor>
                </em>
                ), you can access your edit artist page to manage your artist profile and find the artist booking portal
                section by clicking on the 3 dot menu icon.
              </p>
              <Image
                src="/assets/WCWD ARTIST 3 DOT MENU.png"
                alt="WCWD Artist Menu"
                style={{ maxWidth: '100%', height: 'auto' }}
                radius="md"
                mb="md"
              />
              <Image
                src="/assets/WCWD ARTIST EDIT ARTIST.png"
                alt="WCWD Artist Edit Artist"
                style={{ maxWidth: '100%', height: 'auto' }}
                radius="md"
              />
            </li>
            <li className="mb-4">
              <h3 className="font-semibold mb-2">Enable the Booking Portal</h3>
              <p className="mb-2">
                Look for the toggle switch at the top of the edit artist page, and click it to start to enable your
                booking portal.
              </p>
              <Image
                src="/assets/WCWD ARTIST ACTIVATE PORTAL.png"
                alt="WCWD Artist Activate Portal Image"
                style={{ maxWidth: '100%', height: 'auto' }}
                radius="md"
              />
            </li>
            <li className="mb-4">
              <h3 className="font-semibold mb-2">Create Your Booking Portal Password</h3>
              <p className="mb-2">
                Create your portal password. You will provide the organizers that hire you with the link to your booking
                portal and your portal password.
              </p>
              <Image
                src="/assets/WCWD ARTIST CREATE PORTAL PASSWORD.png"
                alt="WCWD Artist Create Portal Password"
                style={{ maxWidth: '100%', height: 'auto' }}
                radius="md"
                mb="md"
              />
              <Image
                src="/assets/WCWD ARTIST PORTAL PASSWORD.png"
                alt="WCWD Artist Portal Password"
                style={{ maxWidth: '100%', height: 'auto' }}
                radius="md"
              />
            </li>
            <li className="mb-4">
              <h3 className="font-semibold mb-2">Log Into Your Portal</h3>
              <p className="mb-2">Use the portal password you just created to log into your artist booking portal.</p>
              <Image
                src="/assets/WCWD ARTIST ENTER BOOKING PORTAL.png"
                alt="WCWD Artist Portal Login"
                style={{ maxWidth: '100%', height: 'auto' }}
                radius="md"
              />
            </li>
            <li className="mb-4">
              <h3 className="font-semibold mb-2">Update Your Booking Info</h3>
              <p className="mb-2">
                Click on the edit booking portal button and fill out all the sections with your information and
                preferences.
              </p>
              <Image
                src="/assets/WCWD ARTIST EDIT PORTAL.png"
                alt="WCWD Artist Edit Artist Portal"
                style={{ maxWidth: '100%', height: 'auto' }}
                radius="md"
                mb="md"
              />
              <Image
                src="/assets/WCWD ARTIST BOOKING PORTAL INFO.png"
                alt="WCWD Artist Artist Portal Info Fields"
                style={{ maxWidth: '100%', height: 'auto' }}
                radius="md"
              />
            </li>
            <li className="mb-4">
              <h3 className="font-semibold mb-2">Use Your Portal For Current & Future Bookings!</h3>
              <p className="mb-2">
                Once you've set up your portal and are happy with the information, you can direct the organizers booking
                you to this page to access your portal and enjoy a life of less booking hassles so you can focus on
                bringing your art to the world!
              </p>
              <p className="mb-2">
                You can change the password at any time in your edit artist view and clicking on the change portal
                password button.
              </p>
              <Image
                src="/assets/WCWD ARTIST CHANGE PASSWORD.png"
                alt="WCWD Artist Change Portal Password"
                style={{ maxWidth: '100%', height: 'auto' }}
                radius="md"
                mb="md"
              />
              <p className="mb-2">
                You are also able access your booking portal through your user profile and also your artist page.
              </p>
              <Image
                src="/assets/WCWD ARTIST PORTAL USER.png"
                alt="WCWD Artist Portal User Password"
                style={{ maxWidth: '100%', height: 'auto' }}
                radius="md"
                mb="md"
              />
              <Image
                src="/assets/WCWD ARTIST PROFILE PORTAL.png"
                alt="WCWD Artist Artist Show Portal"
                style={{ maxWidth: '100%', height: 'auto' }}
                radius="md"
              />
            </li>
          </ol>
        ),
      },
    ],
  },
];
